import {
  selectIsMultiUsingPromo,
  type BetslipState,
} from "sections/Betting/Betslip/betslipSlice";
import type { Context } from "../models/context";
import { useMemo } from "react";
import { useSelector } from "hooks";
import { uniq } from "lodash";
import type { BetSelectionType } from "types/BetTypes";

const createSelectionLookup =
  (selections: BetSelectionType[]) =>
  (fieldToRetrieve: string): string[] => {
    return uniq(
      selections
        .flatMap((selection) => [
          selection[fieldToRetrieve as keyof typeof selection],
          ...(selection.subOutcomes ?? []).map(
            (subOutcome) =>
              subOutcome[fieldToRetrieve as keyof typeof subOutcome],
          ),
        ])
        .filter(Boolean) as string[],
    );
  };

const betslipToSelectionsContext = (
  betslip: BetslipState,
  outcomeIds: string[],
): Context["selection"] => {
  const isMulti = betslip.betType === "MULTI";

  // @ts-ignore TODO: fix betslip types
  const isMultiUsingPromo = useSelector(selectIsMultiUsingPromo);

  const multiOdds = Object.values(betslip.selections).reduce((prev, curr) => {
    if (prev === 0) return curr.odds;
    return prev * curr.odds;
  }, 0);

  const selections = useMemo(
    () =>
      Object.keys(betslip.selections)
        .filter((outcomeId) => outcomeIds.includes(outcomeId))
        .map((outcomeId) => betslip.selections[outcomeId]),
    [betslip.selections],
  );

  // loop through the selections and subOutcomes of the selection if they exist
  // so we get all data points for singles and SEM's
  const getDataFromAllSelectionTypes = createSelectionLookup(selections);

  const seasonIds = useMemo(
    () => getDataFromAllSelectionTypes("seasonId"),
    [selections],
  );

  const eventIds = useMemo(
    () => getDataFromAllSelectionTypes("eventId"),
    [selections],
  );

  const marketIds = useMemo(
    () => getDataFromAllSelectionTypes("marketId"),
    [selections],
  );

  const meetingIds = useMemo(
    () => getDataFromAllSelectionTypes("meetingId"),
    [selections],
  );

  const sports = useMemo(
    () => getDataFromAllSelectionTypes("sport"),
    [selections],
  );

  const marketTypes = useMemo(
    () => getDataFromAllSelectionTypes("marketType"),
    [selections],
  );

  const tournamentIds = useMemo(
    () => getDataFromAllSelectionTypes("tournamentId"),
    [selections],
  );

  if (isMulti) {
    return {
      isMulti,
      odds: multiOdds,
      stake: betslip.multiStake * 100,
      subOutcomesCount: 0,
      stakeSource: isMultiUsingPromo ? "PROMOTION_USER" : "AVAILABLE",
      seasonIds,
      eventIds,
      outcomeIds,
      marketIds,
      meetingIds,
      tournamentIds,
      sports,
      marketTypes,
      outcomes: Object.values(betslip.selections).map((selection) => {
        return {
          id: selection.outcomeId,
          eventId: selection.eventId,
          odds: selection.odds,
          stake: selection.stake * 100,
          allocationId: selection.allocationId,
          eventType: selection.eventType,
          hasPromotionalMarkets: selection.hasPromotionalMarkets,
          stakeSource: selection.isUsingPromo ? "PROMOTION_USER" : "AVAILABLE",
          subOutcomesCount: (selection.subOutcomes ?? []).length ?? 0,
          expectedResults: selection.expectedResults,
        } satisfies Context["selection"]["outcomes"][0];
      }),
      expectedResults: {},
    };
  }

  // for single there is only 1 outcome id
  const outcomeId = outcomeIds[0];
  const selection = betslip.selections[outcomeId];

  return {
    isMulti,
    odds: selection.odds,
    stake: selection.stake * 100,
    outcomes: [
      {
        id: selection.outcomeId,
        eventId: selection.eventId,
        odds: selection.odds,
        stake: selection.stake * 100,
        allocationId: selection.allocationId,
        eventType: selection.eventType,
        hasPromotionalMarkets: selection.hasPromotionalMarkets,
        stakeSource: selection.isUsingPromo ? "PROMOTION_USER" : "AVAILABLE",
        subOutcomesCount: (selection.subOutcomes ?? []).length ?? 0,
        expectedResults: selection.expectedResults,
      },
    ],
    allocationId: selection.allocationId,
    eventType: selection.eventType,
    hasPromotionalMarkets: selection.hasPromotionalMarkets,
    seasonIds,
    eventIds,
    outcomeIds,
    marketIds,
    meetingIds,
    tournamentIds,
    sports,
    marketTypes,
    stakeSource: selection.isUsingPromo ? "PROMOTION_USER" : "AVAILABLE",
    subOutcomesCount: (selection.subOutcomes ?? []).length ?? 0,
    expectedResults: selection.expectedResults,
    minSelectionOdds: (selection.subOutcomes ?? []).map(
      (outcome) => outcome.odds,
    ),
  };
};

export { betslipToSelectionsContext };
